import type React from "react";

import { ChevronLeft, ChevronRight, Settings, WarningRounded } from "@mui/icons-material";

import { useModuleWarning } from "@framework/internal/components/LeftSettingsPanel/_hooks";
import type { ModuleInstance } from "@framework/ModuleInstance";
import { Banner } from "@lib/components/Banner";
import { Button } from "@lib/components/Button";
import { Tabs } from "@lib/components/Tabs";
import { Tooltip } from "@lib/components/Tooltip";
import { resolveClassNames } from "@lib/utils/resolveClassNames";

type ModuleSettingsHeaderProps = {
    activeModuleInstance: ModuleInstance<any, any> | null;
    activeTab: string | null;
    availableTabs: Record<string, TabConfig>;
    isCollapsed: boolean;
    onExpandClick: () => void;
    onCollapseClick: () => void;
    onTabChange: (tabKey: string) => void;
};

export function ModuleSettingsHeader(props: ModuleSettingsHeaderProps): React.ReactNode {
    const tabKeys = Object.keys(props.availableTabs);
    const activeTab = props.activeTab ? props.availableTabs[props.activeTab] : undefined;

    const activeModuleTitle = props.activeModuleInstance?.getTitle() ?? null;

    const { warningText, isWarningVisible, dismissWarning, showWarning } = useModuleWarning(props.activeModuleInstance);

    function handleWarningIconClick() {
        if (!isWarningVisible) {
            showWarning();
        }
    }

    function handleTabChange(tabKey: string) {
        props.onTabChange(tabKey);
    }

    function makeSettingsTabs() {
        if (tabKeys.length === 0) {
            return (
                <div className="px-2xs flex h-full items-center justify-center">
                    <Settings fontSize="small" />
                </div>
            );
        }

        return (
            <div className="flex h-full items-center pl-px">
                <Tabs.Root onValueChange={handleTabChange}>
                    <Tabs.List size="small">
                        {tabKeys.map((key) => {
                            const config = props.availableTabs[key];
                            return (
                                <Tooltip key={key} content={config.title} side="bottom">
                                    <Tabs.Tab key={key} value={key}>
                                        {config.icon}
                                    </Tabs.Tab>
                                </Tooltip>
                            );
                        })}
                    </Tabs.List>
                </Tabs.Root>
            </div>
        );
    }

    function makeHeaderContent() {
        if (props.isCollapsed) {
            return (
                <Button
                    onClick={props.onExpandClick}
                    title="Expand settings panel"
                    iconOnly
                    tone="neutral"
                    size="small"
                    variant="ghost"
                >
                    <div className="flex flex-row items-center">
                        {activeTab?.icon ?? <Settings fontSize="inherit" />}
                        <ChevronRight fontSize="inherit" />
                    </div>
                </Button>
            );
        }

        const highlightWarning = !!warningText && !isWarningVisible;

        return (
            <>
                {makeSettingsTabs()}
                <span
                    title={activeModuleTitle ?? undefined}
                    className={resolveClassNames(
                        "text-body-sm grow self-center overflow-hidden p-0 text-ellipsis whitespace-nowrap",
                        { "text-neutral-subtle italic": !activeModuleTitle, "font-bolder": !!activeModuleTitle },
                    )}
                >
                    {activeModuleTitle ?? "No module selected"}
                </span>
                {warningText && (
                    <Tooltip content={`Module has warning${highlightWarning ? " (click to view)" : ""}`} delay="medium">
                        {/* Using a span to ensure the tooltip has a child with enabled pointer-events */}
                        <span>
                            <Button
                                variant="ghost"
                                disabled={!highlightWarning}
                                tone="neutral"
                                iconOnly
                                onClick={handleWarningIconClick}
                            >
                                <WarningRounded fontSize="inherit" />
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Button
                    onClick={props.onCollapseClick}
                    title="Collapse settings panel"
                    iconOnly
                    tone="neutral"
                    size="small"
                    variant="ghost"
                >
                    <ChevronLeft fontSize="inherit" />
                </Button>
            </>
        );
    }

    return (
        <div className="flex flex-col">
            <div
                className={resolveClassNames("gap-x-md pr-2xs bg-canvas flex h-10 items-center", {
                    "pl-xs": props.isCollapsed,
                })}
            >
                {makeHeaderContent()}
            </div>
            {!props.isCollapsed && isWarningVisible && warningText && (
                <WarningBanner text={warningText} onDismiss={dismissWarning} />
            )}
        </div>
    );
}

type WarningBannerProps = {
    text: string;
    onDismiss: () => void;
};

function WarningBanner(props: WarningBannerProps): React.ReactNode {
    return (
        <div className="px-2xs py-2xs">
            <Banner tone="warning" dismissable={true} onDismiss={props.onDismiss}>
                <strong>Note:</strong> {props.text}
            </Banner>
        </div>
    );
}

type TabConfig = {
    title: string;
    icon: React.ReactElement;
};
